import {
  ApplicationScope,
  Customer,
  CustomerFinePrints,
  CustomerShort, Imprint,
  Order,
  Role, SmtpSettings,
} from '~/api-client';
import { defineStore } from "pinia";
import { errorMsg, handleFileDownload } from "~/modules/common/services/util/util";
import {ExtendedCurrentOrder} from "~/modules/common/stores/orders";
import { UploadUserFile } from "element-plus";

interface State {
  _currentCustomer: Customer,
  _ownOrderHistory: Order[],
  _ownOrderById: { [id: string]: Order };
  _ownExtendedOrders: ExtendedCurrentOrder[],
  _allCustomers: CustomerShort[],
  _customerById: { [id: string]: CustomerShort}
  _customerFinePrints: CustomerFinePrints,
  _smtpSettings: SmtpSettings,
}

export const useCustomerStore = defineStore('customer', {
  state: (): State => ({
    _allCustomers: [],
    _customerById: {},
    _currentCustomer: {},
    _ownOrderHistory: [],
    _ownOrderById: {},
    _ownExtendedOrders: [],
    _customerFinePrints: {},
    _smtpSettings: {}
  }),

  getters: {
    currentCustomer: (state) => state._currentCustomer,
    orderHistory: (state) => state._ownOrderHistory,
    ownOrderById: (state) => (orderId: string) => state._ownOrderById[orderId],
    extendedOrders: (state) => state._ownExtendedOrders,
    allCustomers: (state) => state._allCustomers,
    customerById: (state) => (customerId: string) => state._customerById[customerId],
    customerFinePrints: (state) => state._customerFinePrints,
    allCustomerFinePrintsFilled: (state) => state._customerFinePrints.imprint && state._customerFinePrints.uploadedDataPrivacyPdf && state._customerFinePrints.uploadedTermsUsagePdf,
    smtpSettings: (state) => state._smtpSettings
  },

  actions: {
    async getCustomers(): Promise<CustomerShort[]>{
      const {$backendService, $logger, $tenant}  = useNuxtApp();

      return $backendService.customerControllerApi.getCustomers({xApplicationScope: $tenant.getTenant})
        .then((result) => {
          const customerById : {[id: string]: CustomerShort} = {}
          result.forEach((res: CustomerShort) => {
            customerById[res.id!] = res;
          })
          this.$state._customerById = customerById;
          this.$state._allCustomers = result;
          $logger.info("loaded customers");
          return result;
        })
        .catch((error) => {
          $logger.error("error fetching customers", error);
          throw error;
        });
    },
    getCurrentCustomer(){
      const {$backendService, $logger, $tenant}  = useNuxtApp();

      return $backendService.customerControllerApi.getCustomer({xApplicationScope: $tenant.getTenant}).then((customer) => {
        this.$state._currentCustomer = customer;
        return customer;
      }).catch((error) => {
        $logger.error('error fetching customer');
        throw error;
      });
    },
    partiallyUpdateCustomerById(userId: string, customer: Customer){
      const {$backendService, $logger, $tenant}  = useNuxtApp();
      return $backendService.customerControllerApi.partiallyUpdateCustomerById({
        xApplicationScope: $tenant.getTenant,
        id: userId!,
        customer: customer
      }).catch(error => {
        $logger.error('error', error);
        throw error;
      })
    },
    updateCustomerRoles(customerShort: CustomerShort, roles: Role[]){
      const {$backendService, $logger, $tenant}  = useNuxtApp();

      return $backendService.customerControllerApi.updateCustomerRoles({
        xApplicationScope: $tenant.getTenant,
        id: customerShort.id!,
        role: roles
      }).catch(error => {
        ElMessage.error("Beim Ändern der Rolle ist es zu einem Fehler gekommen");
        throw error;
      });
    },
    updateCustomerReferenceById(userId: string, reference: string){
      const {$backendService, $logger, $tenant} = useNuxtApp();

      return $backendService.customerControllerApi.updateCustomerReferenceById({
        xApplicationScope: $tenant.getTenant,
        id: userId!,
        customer: {
          reference: reference
        }
      }).catch(error => {
        errorMsg(error).then(error_msg => ElMessage.error(error_msg));
        throw error;
      })
    },
    markCustomerFreeOfCharge(customerShort: CustomerShort){
      const {$backendService, $logger, $tenant}  = useNuxtApp();

      return $backendService.customerControllerApi.markCustomerFreeOfCharge({
        xApplicationScope: $tenant.getTenant,
        id: customerShort.id!
      }).catch(error => {
        ElMessage.error("Beim Entbinden des Users von der Zahlungsverpflichtung ist es zu einem Fehler gekommen");
        throw error;
      })
    },
    unmarkCustomerFreeOfCharge(customerShort: CustomerShort){
      const {$backendService, $logger, $tenant}  = useNuxtApp();

      return $backendService.customerControllerApi.unmarkCustomerFreeOfCharge({
        xApplicationScope: $tenant.getTenant,
        id: customerShort.id!
      }).catch(error => {
        ElMessage.error("Beim Hinzufügen des Users zur Zahlungsverpflichtung ist es zu einem Fehler gekommen");
        throw error;
      })
    },
    lockCustomer(customerShort: CustomerShort){
      const {$backendService, $logger, $tenant}  = useNuxtApp();

      return $backendService.customerControllerApi.lockCustomer({
        xApplicationScope: $tenant.getTenant,
        id: customerShort.id!
      }).catch(error => {
        ElMessage.error("Beim Sperren des Users ist es zu einem Fehler gekommen");
        throw error;
      })
    },
    unlockCustomer(customerShort: CustomerShort){
      const {$backendService, $logger, $tenant}  = useNuxtApp();

      return $backendService.customerControllerApi.unlockCustomer({
        xApplicationScope: $tenant.getTenant,
        id: customerShort.id!
      }).catch(error => {
        ElMessage.error("Beim Entsperren des Users ist es zu einem Fehler gekommen");
        throw error;
      })
    },
    async getOrdersByCustomer(customerId: string): Promise<Order[]>{
      const {$backendService, $logger, $tenant, $customerStore} = useNuxtApp();
      return $backendService.customerControllerApi.getOrdersByCustomer({ xApplicationScope: $tenant.getTenant, id: customerId })
          .then(response => {
            if (customerId === $customerStore.currentCustomer.id) this.$state._ownOrderHistory = response;
            return response
          });
    },
    async getOwnOrders(): Promise<Order[]>{
      const {$customerStore} = useNuxtApp();
      return this.getOrdersByCustomer($customerStore.currentCustomer.id!).then(orders => {
        const ownOrders: { [id: string]: Order} = {}
        orders.forEach((order: Order) => {
          ownOrders[order.id!] = order;
        })
        this.$state._ownOrderById = ownOrders;
        this.$state._ownOrderHistory = orders;
        return orders;
      });

    },
    queryCustomersByQueryObject(queriedUser: Customer){
      const {$backendService, $logger, $tenant} = useNuxtApp();

      return $backendService.customerControllerApi.queryCustomersByQueryObject({
        xApplicationScope: $tenant.getTenant!,
        customer: queriedUser,
      });
    },
    updateCustomerKeyInAllCustomersState(customerId: string, key: keyof CustomerShort, result: any ){
      this.$state._allCustomers = this.$state._allCustomers.map(customer => {
        if (customer.id === customerId){
          customer[key] = result;
        }
        return customer;
      })
    },
    getCurrentOrders(){
      const {$backendService, $logger, $tenant} = useNuxtApp();

      return $backendService.customerControllerApi.getCurrentOrders({
        xApplicationScope: $tenant.getTenant
      });
    },
    triggerUpdatePasswordEmailForOtherUser (customerId: string){
      const {$backendService, $logger, $tenant} = useNuxtApp();

      return $backendService.customerControllerApi.resetKeycloakPasswordForOtherUser(
        {xApplicationScope: $tenant.getTenant, customerId: customerId})
    },
    async getCustomerFinePrints(customerId: string) {
      const {$backendService, $logger, $tenant} = useNuxtApp();
      return $backendService.customerControllerApi.getCustomerFinePrints({xApplicationScope: $tenant.getTenant, customerId: customerId})
        .then((result:CustomerFinePrints) => {
          this.$state._customerFinePrints = result;
        }).catch(error => {
          $logger.error('unable to get customer fine prints', error);
          ElMessage.error("Beim Abrufen Ihres Kleingedrucktes ist ein Fehler aufgetreten.");
          throw error;
        })
    },
    async getCustomerImprint(customerId: string){
      const {$backendService, $logger, $tenant} = useNuxtApp();
      return $backendService.customerControllerApi.getCustomerImprint({xApplicationScope: $tenant.getTenant, customerId: customerId})
        .catch(error => {
          $logger.error('unable to get customer imprints', error);
          ElMessage.error("Beim Abruf von Ihrem Impressum ist leider zu einem Fehler gekommen.");
          throw error;
        })
    },
    async updateCustomerImprint(customerId: string, imprint: string){
      const {$backendService, $logger, $tenant} = useNuxtApp();
      return $backendService.customerControllerApi.updateCustomerImprint({
        xApplicationScope: $tenant.getTenant, customerId: customerId, imprint: { text: imprint }})
        .then((result: Imprint) => {
          this.$state._customerFinePrints.imprint = result.text;
          ElMessage.success('Das Impressum wurde erfolgreich bearbeitet');
        }).catch(error => {
          $logger.error('unable to update imprints', error);
          ElMessage.error("Beim Aktualisieren Ihres Impressums ist leider ein Fehler aufgetreten.");
          throw error;
        })
    },
    async getCustomerDataPrivacyPdf(customerId: string){
      const {$backendService, $logger, $tenant} = useNuxtApp();
      return $backendService.customerControllerApi.getCustomerDataPrivacyPdf({xApplicationScope: $tenant.getTenant, customerId: customerId})
        .then((response: Blob) => {
          handleFileDownload(response, `datenschutz_${customerId}.pdf`);
        }).catch(error => {
          $logger.error('unable to download terms', error);
          ElMessage.error("Beim Herunterladen der Datenschutzerklärung ist ein Fehler aufgetreten");
          throw error;
        })
    },
    async updateCustomerDataPrivacyPdf(customerId: string, file: UploadUserFile){
      const {$backendService  , $logger, $tenant} = useNuxtApp();

      return $backendService.customerControllerApi.updateCustomerDataPrivacyPdf({
        xApplicationScope: $tenant.getTenant,
        customerId: customerId,
        filename: file.name,
        body: file.raw!
      }).then(result => {
        ElMessage.success('Die Datenschutzerklärung wurde erfolgreich hochgeladen');
        this.$state._customerFinePrints.uploadedDataPrivacyPdf = true;
        this.$state._customerFinePrints.uploadedDataPrivacyName = file.name;
      }).catch(error => {
        $logger.error('unable to download data privacy', error);
        ElMessage.error('Beim Aktualisieren der Datenschutzerklärung ist ein Fehler aufgetreten');
      })
    },
    async deleteCustomerDataPrivacyPdf(customerId: string, activeRemove: boolean = true){
      const {$backendService  , $logger, $tenant} = useNuxtApp();
      return $backendService.customerControllerApi.deleteCustomerDataPrivacyPdf({
        xApplicationScope: $tenant.getTenant,
        customerId: customerId
      }).then(() => {
        if (activeRemove){
          ElMessage.success('Die Datenschutzerklärung wurde erfolgreich gelöscht');
        }
        this.$state._customerFinePrints.uploadedDataPrivacyPdf = false;
        this.$state._customerFinePrints.uploadedDataPrivacyName = "";
      }).catch((error:any) => {
        $logger.error('unable to delete data privacy', error);
        ElMessage.error('Beim Löschen der Datenschutzerklärung ist ein Fehler aufgetreten');
      })
    },
    async getCustomerSmtpSettings(){
      const {$backendService, $logger, $tenant} = useNuxtApp();
      return $backendService.smtpControllerApi.getCustomerSmtpSettings({xApplicationScope: $tenant.getTenant})
        .then((response: SmtpSettings) => {
          this.$state._smtpSettings = response;
          return response;
        }).catch(error => {
          $logger.error('unable to get smtp settings', error);
          ElMessage({duration: 5000, message: "Beim Laden der SMTP Einstellungen ist ein Fehler aufgetreten", type: 'error'})
          throw error;
        })
    },
    async updateCustomerSmtpSettings(settings: SmtpSettings){
      const {$backendService, $logger, $tenant} = useNuxtApp();
      return $backendService.smtpControllerApi.updateCustomerSmtpSettings({
        xApplicationScope: $tenant.getTenant,
        smtpSettings: settings
      }).then(response => {
        ElMessage.success('Die SMTP Einstellungen wurden erfolgreich aktualisiert');
        this.$state._smtpSettings = response;
        return response;
      }).catch(error => {
        $logger.error('unable to update SMTP settings', error);
        ElMessage.error('Beim Aktualisieren der SMTP Einstellungen ist ein Fehler aufgetreten');
      })
    },
    async deleteCustomerSmtpSettings(){
      const {$backendService, $logger, $tenant} = useNuxtApp();
      return $backendService.smtpControllerApi.deleteCustomerSmtpSettings({
        xApplicationScope: $tenant.getTenant
      }).then(response => {
        ElMessage.success('Die SMTP Einstellungen wurden erfolgreich entfernt');
        this.$state._smtpSettings = response;
        return response;
      }).catch(error => {
        $logger.error('unable to delete SMTP settings', error);
        ElMessage.error('Beim Löschen der SMTP Einstellungen ist ein Fehler aufgetreten');
      })
    },
    async testCustomerSmtpSettings(){
      const {$backendService, $logger, $tenant} = useNuxtApp();
      return $backendService.smtpControllerApi.testCustomerSmtpSettings({
        xApplicationScope: $tenant.getTenant
      }).then(response => {
        ElMessage.success('Ihre SMTP Einstellungen wurden erfolgreich getestet.');
        return response;
      }).catch(error => {
        $logger.error('error while testing SMTP settings', error);
        ElMessage.error(`Die Test E-Mail konnte nicht versendet werden. Bitte prüfen Sie die Einstellungen!`);
      })
    },
    async getCustomerTermsUsagePdf(customerId: string){
      const {$backendService, $logger, $tenant} = useNuxtApp();
      return $backendService.customerControllerApi.getCustomerTermsUsagePdf({xApplicationScope: $tenant.getTenant, customerId: customerId})
        .then((response: Blob) => {
          handleFileDownload(response, `nutzungsbedingungen_${customerId}.pdf`);
        }).catch(error => {
          $logger.error('unable to download terms usage', error);
          ElMessage.error("Beim Herunterladen der Nutzungsbedingungen ist ein Fehler aufgetreten");
          throw error;
        })
    },
    async updateCustomerTermsUsagePdf(customerId: string, file: UploadUserFile){
      const {$backendService  , $logger, $tenant} = useNuxtApp();
      return $backendService.customerControllerApi.updateCustomerTermsUsagePdf({
        xApplicationScope: $tenant.getTenant,
        customerId: customerId,
        filename: file.name,
        body: file.raw!
      }).then(result => {
        ElMessage.success('Die Nutzungsbedingungen wurden erfolgreich hochgeladen');
        this.$state._customerFinePrints.uploadedTermsUsagePdf = true;
        this.$state._customerFinePrints.uploadedTermsUsageName = file.name;
      }).catch(error => {
        $logger.error('unable to update terms usage', error);
        ElMessage.error('Beim Aktualisieren der Nutzungsbedingungen ist ein Fehler aufgetreten');
      })
    },
    async deleteCustomerTermsUsagePdf(customerId: string, activeRemove: boolean = true){
      const {$backendService  , $logger, $tenant} = useNuxtApp();
      return $backendService.customerControllerApi.deleteCustomerTermsUsagePdf({
        xApplicationScope: $tenant.getTenant,
        customerId: customerId
      }).then(() => {
        if (activeRemove){
          ElMessage.success('Die Nutzungsbedingungen wurden erfolgreich gelöscht');
        }
        this.$state._customerFinePrints.uploadedTermsUsagePdf = false;
        this.$state._customerFinePrints.uploadedTermsUsageName = "";
      }).catch((error:any) => {
        $logger.error('unable to delete terms usage', error);
        ElMessage.error('Beim Löschen der Nutzungsbedingungen ist ein Fehler aufgetreten');
      })
    },
    async updateDeactivationOrDeletionNotificationDate(customerId: string, customer: Customer){
      const {$backendService  , $logger, $tenant} = useNuxtApp();
      return $backendService.customerControllerApi.updateCustomerDeactivationOrDeletionNotificationDate({
        xApplicationScope: $tenant.getTenant,
        id: customerId,
        customer: customer
      }).then(result => {
        ElMessage.success('Die Kundigungs- und/oder Löschdatum wurde erfolgreich bearbeitet');

        return result;
      }).catch((error:any) => {
        $logger.error('unable to change deactivation or deletion notification date', error);
        errorMsg(error).then(error_msg => ElMessage({duration: 5000, message: error_msg, type: 'error'}));
      })
    }
  }
})
